import { getFieldCopy, type LocaleKey } from "@/i18n";
import { CdcSelectField, type CdcFieldCopy } from "./cdc-field-helpers";
import { TextField, type TextFieldProps } from "../text-field";
import { type CommonCopy } from "@/i18n";
import STATE_OPTIONS from "@/data/us-states.json" with { type: "json" };
import {
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { FormField } from "@/components/ui/form";
import { cn } from "@/lib/utils";

export type CdcProfessionalNumberFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  TextFieldProps<TFieldValues, TName>,
  | "fieldKey"
  | "options"
  | "label"
  | "placeholder"
  | "requiredLabel"
  | "description"
> & {
  locale: LocaleKey;
  stateDropdown: boolean;
  control: Control<TFieldValues>;
  name: TName;
  required?: boolean;
};

export function CdcProfessionalNumberField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  locale,
  stateDropdown,
  control,
  name,
  required = false,
  className,
  ...rest
}: Readonly<CdcProfessionalNumberFieldProps<TFieldValues, TName>>) {
  const baseFieldName = name as string;

  const { label, placeholder } = getFieldCopy(
    locale,
    "professionalNumber",
  ) as unknown as CdcFieldCopy;
  const { errors: requiredLabel } = getFieldCopy(
    locale,
    "common",
  ) as unknown as CommonCopy;

  return (
    <div
      className={cn(
        stateDropdown && "grid grid-cols-1 items-start gap-4 md:grid-cols-2",
        className,
      )}
      {...rest}
    >
      {stateDropdown && (
        <FormField
          control={control}
          name={`${baseFieldName}.state` as FieldPath<TFieldValues>}
          render={({ field }) => (
            <CdcSelectField
              {...field}
              control={control}
              name={`${baseFieldName}.state` as TName}
              fieldKey="state"
              locale={locale}
              options={STATE_OPTIONS}
            />
          )}
        />
      )}
      <FormField
        control={control}
        name={`${baseFieldName}.number` as FieldPath<TFieldValues>}
        render={({ field }) => (
          <TextField
            {...field}
            control={control}
            name={`${baseFieldName}.number` as TName}
            label={label}
            placeholder={placeholder}
            required={required}
            requiredLabel={requiredLabel.required}
          />
        )}
      />
    </div>
  );
}
